import {
  ACCOUNT_PREFERENCE_KEYS,
  filterAccountPreferences,
  type Preferences,
  type PreferenceKey,
} from "../preferences";
import { defaultMetadata } from "./metadata";
import { sameTarget, type MetadataPatch, type Connection } from "./model";
import type { ApiClient } from "./api";
import type { DocumentStore } from "./store";
interface AccountData {
  target?: Connection | null;
  ui: Preferences;
  metadata: MetadataPatch | null;
}
/** The entries of a stored patch that hold a value; null ones were forgotten. */
const present = (entries: Record<string, string | null> = {}): Record<string, string> =>
  Object.fromEntries(
    Object.entries(entries).filter((entry): entry is [string, string] => entry[1] !== null),
  );
interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
export class AccountDataSync {
  private owner = "";
  private pending: Preferences = {};
  private timer?: ReturnType<typeof setTimeout>;
  private busy = false;
  private applying = false;
  private ready = false;
  constructor(
    private store: DocumentStore,
    private api: ApiClient,
    private storage: Storage,
    private applyUI: () => void,
  ) {
    this.owner = storage.getItem("turbonotes2-account-owner") ?? "";
    this.pending = this.readPending();
    store.addEventListener("change", () => this.schedule());
  }
  private identity() {
    return String(
      this.api.session.user?.id ?? this.api.session.user?.login ?? "",
    );
  }
  private get key() {
    return `turbonotes2-account-pending:${this.owner}`;
  }
  private readPending(): Preferences {
    return Object.fromEntries(
      ACCOUNT_PREFERENCE_KEYS.map((key) => [
        key,
        this.storage.getItem(`${this.key}:${key}`),
      ]).filter(([, value]) => value !== null && value !== ""),
    ) as Preferences;
  }
  record(key: string, value: string) {
    const name = key.replace(/^turbonotes2-/, "") as PreferenceKey;
    if (
      this.applying ||
      !this.owner ||
      (this.identity() && this.owner !== this.identity()) ||
      !(ACCOUNT_PREFERENCE_KEYS as readonly string[]).includes(name)
    )
      return;
    this.pending[name] = value;
    // One outbox key per preference prevents tabs overwriting each other's
    // queued changes. Values survive reloads and offline launches.
    this.storage.setItem(`${this.key}:${name}`, value);
    this.schedule();
  }
  private apply(ui: Preferences) {
    this.pending = this.readPending();
    this.applying = true;
    try {
      let changed = false;
      for (const [key, value] of Object.entries(filterAccountPreferences({ ...ui, ...this.pending }))) {
        if (this.storage.getItem(`turbonotes2-${key}`) === value) continue;
        this.storage.setItem(`turbonotes2-${key}`, value!);
        changed = true;
      }
      if (changed) this.applyUI();
    } finally {
      this.applying = false;
    }
  }
  private async metadata(value: MetadataPatch, acknowledged?: MetadataPatch) {
    await this.store.database.applyMetadata(
      {
        ...defaultMetadata(),
        pinned: Object.entries(value.pins ?? {})
          .filter(([, pin]) => pin)
          .map(([path]) => path),
        checkpoint: { auto_minutes: value.auto_minutes ?? 30 },
        icons: present(value.icons),
        custom_icons: present(value.custom_icons),
      },
      acknowledged,
    );
    await this.store.changed();
  }
  async load() {
    const owner = this.identity();
    if (!owner) {
      this.ready = false;
      return;
    }
    this.ready = false;
    this.owner = owner;
    this.pending = this.readPending();
    const previousOwner = this.storage.getItem("turbonotes2-account-owner");
    const data = await this.api.request<AccountData>("/preferences");
    if (this.identity() !== owner) return;
    // Import this device's preferences only where the account has none. A
    // different account never inherits this browser's previous user's choices.
    if (!previousOwner || previousOwner === owner) {
      for (const key of ACCOUNT_PREFERENCE_KEYS) {
        const local = this.storage.getItem(`turbonotes2-${key}`);
        if (
          data.ui[key] === undefined &&
          local !== null &&
          this.pending[key] === undefined
        )
          this.record(`turbonotes2-${key}`, local);
      }
    }
    this.storage.setItem("turbonotes2-account-owner", owner);
    this.apply(data.ui);
    if (
      sameTarget(
        this.store.state?.vault.connection ?? null,
        data.target === undefined ? this.api.session.connection : data.target,
      )
    )
      await this.metadata(data.metadata ?? {});
    this.ready = true;
    this.schedule();
  }
  close() {
    this.ready = false;
    clearTimeout(this.timer);
  }
  schedule() {
    if (!this.ready || this.busy) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush().catch(() => {}), 750);
  }
  async flush() {
    if (
      !this.ready ||
      this.busy ||
      !navigator.onLine ||
      !this.owner ||
      this.owner !== this.identity()
    )
      return;
    const state = await this.store.database.load();
    if (this.busy || this.owner !== this.identity()) return;
    this.pending = this.readPending();
    const target = state.vault.connection;
    const metadata =
      (state.vault.userId === undefined ||
        state.vault.userId === this.api.session.user?.id) &&
      sameTarget(target, this.api.session.connection)
        ? structuredClone(state.metadata.pending ?? {})
        : {};
    for (const field of ["pins", "icons", "custom_icons"] as const)
      if (metadata[field] && !Object.keys(metadata[field]).length) delete metadata[field];
    const ui = { ...this.pending };
    if (!Object.keys(ui).length && !Object.keys(metadata).length) return;
    this.busy = true;
    const owner = this.owner;
    try {
      const data = await this.api.request<AccountData>("/preferences", "POST", {
        ui,
        ...(Object.keys(metadata).length ? { metadata, target } : {}),
      });
      if (this.identity() !== owner) return;
      for (const [key, value] of Object.entries(ui))
        if (this.storage.getItem(`${this.key}:${key}`) === value)
          this.storage.setItem(`${this.key}:${key}`, "");
      this.apply(data.ui);
      if (
        data.metadata &&
        sameTarget(
          (await this.store.database.load()).vault.connection,
          data.target === undefined ? target : data.target,
        )
      )
        await this.metadata(data.metadata, metadata);
    } finally {
      this.busy = false;
    }
    // Catch edits made while the previous request was in flight.
    this.schedule();
  }
}
